import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { content } from '../data/content';

const HeaderContainer = styled.header`
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: #000000;
  background-image: 
    repeating-linear-gradient(
      0deg,
      transparent,
      transparent 40px,
      rgba(138, 138, 126, 0.04) 40px,
      rgba(138, 138, 126, 0.04) 41px
    );
  border-bottom: 2px solid #b7ab9f;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  overflow: visible; /* Allow mobile menu to extend below header */

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      linear-gradient(90deg, rgba(138, 138, 126, 0.03) 0%, transparent 15%, transparent 85%, rgba(138, 138, 126, 0.03) 100%);
    pointer-events: none;
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(138, 138, 126, 0.2), transparent);
    z-index: 2;
  }
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem 0;
  max-width: 1200px;
  margin: 0 auto;
  padding-left: 20px;
  padding-right: 20px;
  position: relative;
  z-index: 10000;
`;

const Logo = styled.div`
  margin-right: auto;
  padding-right: 3rem;
  cursor: pointer;
  
  @media (max-width: 768px) {
    padding-right: 1rem;
  }
  
  img {
    height: 90px;
    width: auto;
    max-width: 350px;
    background: transparent;
    border-radius: 4px;
    transition: all 0.3s ease;
    
    @media (max-width: 768px) {
      height: 60px;
      max-width: 200px;
    }

    @media (max-width: 480px) {
      height: 50px;
      max-width: 160px;
    }

    @media (max-width: 360px) {
      height: 40px;
      max-width: 130px;
    }
  }

  &:hover img {
    opacity: 0.8;
    transform: scale(1.02);
  }
`;

const LogoFallback = styled.div`
  height: 90px;
  width: 350px;
  background: transparent;
  border: 2px solid #b7ab9f;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b7ab9f;
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    height: 60px;
    width: 200px;
    font-size: 1rem;
  }

  &:hover {
    background: #b7ab9f;
    color: #ffffff;
  }
`;

// Desktop Navigation
const DesktopNav = styled.div`
  display: flex;
  align-items: center;
  gap: 2.5rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

// Mobile Menu Button
const MobileMenuButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 1.8rem;
  cursor: pointer;
  z-index: 10002;
  position: relative;
  padding: 0.5rem;
  min-width: 44px;
  min-height: 44px;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    display: flex;
  }
  
  &:hover {
    color: #b7ab9f;
    transition: color 0.3s ease;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

// Mobile Menu Overlay
const MobileMenuOverlay = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9998;
  }
`;

// Mobile Menu Container - positioned relative to header
const MobileMenu = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #000000;
    flex-direction: column;
    padding: 2rem;
    gap: 1.5rem;
    border-top: 1px solid #b7ab9f;
    border-bottom: 1px solid #b7ab9f;
    z-index: 10001;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(10px);
    animation: ${props => props.isOpen ? 'slideDown' : 'slideUp'} 0.3s ease-out;
    
    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }
`;

const NavLink = styled.a`
  color: #ffffff;
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  transition: color 0.3s ease;
  cursor: pointer;
  padding: 0.5rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    color: #b7ab9f;
  }
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    padding: 1rem 0;
    border-bottom: 1px solid rgba(183, 171, 159, 0.2);
    
    &:last-of-type {
      border-bottom: none;
    }
  }
`;

const LanguageToggle = styled.button`
  background: transparent;
  border: 2px solid #b7ab9f;
  color: #b7ab9f;
  padding: 0.7rem 1.2rem;
  border-radius: 3px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    background: #b7ab9f;
    color: #ffffff;
  }
  
  @media (max-width: 768px) {
    margin-top: 1rem;
    padding: 1rem 1.5rem;
    font-size: 1rem;
    width: 100%;
  }
`;

const Header = () => {
  const { language, toggleLanguage } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const t = content[language];

  const scrollToSection = (sectionId) => {
    // If we're not on the home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const headerHeight = 120;
          const elementPosition = element.offsetTop - headerHeight;

          window.scrollTo({
            top: elementPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      // We're already on the home page, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        const headerHeight = 120;
        const elementPosition = element.offsetTop - headerHeight;

        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        });
      }
    }
    setMobileMenuOpen(false);
  };

  const handleLogoError = () => {
    setLogoError(true);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(prev => !prev);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    navigate('/');
    setMobileMenuOpen(false);
  };

  return (
    <>
      <HeaderContainer>
        <Nav>
          <Logo onClick={handleLogoClick}>
            {!logoError ? (
              <img
                src="/images/LogoHorizontal.jpg"
                alt="Groupe Garant"
                onError={handleLogoError}
              />
            ) : (
              <LogoFallback>
                GROUPE GARANT
              </LogoFallback>
            )}
          </Logo>

          {/* Desktop Navigation */}
          <DesktopNav>
            <NavLink onClick={() => scrollToSection('home')}>{t.nav.home}</NavLink>
            <NavLink onClick={() => scrollToSection('about')}>{t.nav.about}</NavLink>
            <NavLink onClick={() => scrollToSection('services')}>{t.nav.services}</NavLink>
            <NavLink onClick={() => scrollToSection('contact')}>{t.nav.contact}</NavLink>
            <LanguageToggle onClick={toggleLanguage}>
              {language === 'fr' ? 'EN' : 'FR'}
            </LanguageToggle>
          </DesktopNav>

          {/* Mobile Menu Button */}
          <MobileMenuButton onClick={toggleMobileMenu}>
            {mobileMenuOpen ? '✕' : '☰'}
          </MobileMenuButton>
        </Nav>

        {/* Mobile Menu - inside HeaderContainer */}
        <MobileMenu isOpen={mobileMenuOpen}>
          <NavLink onClick={() => scrollToSection('home')}>{t.nav.home}</NavLink>
          <NavLink onClick={() => scrollToSection('about')}>{t.nav.about}</NavLink>
          <NavLink onClick={() => scrollToSection('services')}>{t.nav.services}</NavLink>
          <NavLink onClick={() => scrollToSection('contact')}>{t.nav.contact}</NavLink>
          <LanguageToggle onClick={toggleLanguage}>
            {language === 'fr' ? 'EN' : 'FR'}
          </LanguageToggle>
        </MobileMenu>
      </HeaderContainer>

      {/* Mobile Menu Overlay */}
      <MobileMenuOverlay isOpen={mobileMenuOpen} onClick={closeMobileMenu} />
    </>
  );
};

export default Header; 
